import React, { useEffect, useMemo, useState } from "react";
import { SortableTh, useTableSort } from "@/components/SortableTh";
import { api, downloadApiFile, formatApiErrorDetail } from "@/lib/api";
import { decaPdfPath } from "@/lib/publicUrls";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { Field, PageHeader } from "@/components/common";
import { StatusBadge } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, FileText, Eye, RefreshCcw, FileSpreadsheet, Table2, LineChart, FilePlus2 } from "lucide-react";
import { toast } from "sonner";
import DatePicker from "@/components/DatePicker";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import DecaTimeline from "@/components/DecaTimeline";
import { fmtFechaHora, ymdMadrid } from "@/lib/fechas";
import { fmtNumeroDeca } from "@/lib/textos";

const DECA_SORT = {
  id: (d) => d.numero_deca || d.id || "",
  fecha: (d) => d.created_at || "",
  cargador: (d) => d.cargador_snapshot?.razon_social || "",
  transportista: (d) => d.transportista_snapshot?.razon_social || "",
  envios: (d) => Number(d.num_envios) || 0,
  matriculas: (d) => (d.matriculas || []).join(", "),
  version: (d) => Number(d.version) || 0,
  estado: (d) => d.estado || "",
};

const FETCH_SIZE = 1000;

function matchesDeca(d, q) {
  if (q.estado && d.estado !== q.estado) return false;
  const fecha = ymdMadrid(d.created_at);
  if (q.desde && fecha < q.desde) return false;
  if (q.hasta && fecha > q.hasta) return false;
  const b = (q.buscar || "").trim().toLowerCase();
  if (!b) return true;
  const hay = [
    d.id,
    d.numero_deca,
    d.cargador_snapshot?.razon_social,
    d.cargador_snapshot?.nif,
    d.transportista_snapshot?.razon_social,
    d.transportista_snapshot?.nif,
    (d.matriculas || []).join(" "),
    d.estado,
    `v${d.version}`,
  ].join(" ").toLowerCase();
  return hay.includes(b);
}

export default function DecaListPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const puedeManual = user?.rol && user.rol !== "conductor";
  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState({ desde: "", hasta: "", cargador: "", transportista: "", estado: "", buscar: "" });
  const [view, setView] = useState("table");
  const [exporting, setExporting] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api/v1/decas", { params: { limit: FETCH_SIZE, offset: 0 } });
      setAllItems(data.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const filtered = useMemo(() => allItems.filter((d) => matchesDeca(d, q)), [allItems, q]);
  const { sorted, sort, toggle } = useTableSort(filtered, DECA_SORT);

  const exportFile = async (kind) => {
    const params = Object.fromEntries(Object.entries(q).filter(([_, v]) => v !== ""));
    const path = kind === "xlsx" ? "/api/v1/decas/export/xlsx" : "/api/v1/decas/export/zip";
    const fallbackName = kind === "xlsx" ? "deca_export.xlsx" : "deca_export.zip";
    setExporting(kind);
    try {
      await downloadApiFile(path, { params, fallbackName });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo descargar el archivo");
    } finally {
      setExporting(null);
    }
  };

  return (
    <div>
      <PageHeader
        title="Histórico de DeCA"
        subtitle="Todos los documentos emitidos, con versionado, estado y descarga."
        testid="page-historico"
        actions={<>
          {puedeManual ? (
          <Button className="bg-blue-700 hover:bg-blue-800" onClick={()=>nav("/decas/manual")} data-testid="btn-deca-manual">
            <FilePlus2 className="h-4 w-4 mr-2"/> DeCA manual
          </Button>
          ) : null}
          <Tabs value={view} onValueChange={setView}>
            <TabsList data-testid="view-tabs">
              <TabsTrigger value="table" data-testid="tab-table"><Table2 className="h-3.5 w-3.5 mr-1.5"/> Tabla</TabsTrigger>
              <TabsTrigger value="timeline" data-testid="tab-timeline"><LineChart className="h-3.5 w-3.5 mr-1.5"/> Timeline</TabsTrigger>
            </TabsList>
          </Tabs>
          <Button variant="outline" onClick={() => exportFile("xlsx")} disabled={!!exporting} data-testid="btn-export-xlsx"><FileSpreadsheet className="h-4 w-4 mr-2"/> {exporting === "xlsx" ? "Descargando…" : "Excel"}</Button>
          <Button variant="outline" onClick={() => exportFile("zip")} disabled={!!exporting} data-testid="btn-export"><Download className="h-4 w-4 mr-2"/> {exporting === "zip" ? "Descargando…" : "ZIP"}</Button>
          <Button variant="outline" onClick={load} disabled={loading}><RefreshCcw className={`h-4 w-4 mr-2 ${loading ? "animate-spin":""}`}/> Actualizar</Button>
        </>}
      />

      <Card className="p-4 mb-4">
        <div className="flex flex-col lg:flex-row lg:flex-nowrap lg:items-end gap-2">
          <div className="min-w-0 flex-1">
            <Field label="Buscar">
              <Input value={q.buscar} onChange={(e)=>setQ({...q, buscar:e.target.value})} placeholder="DeCA-2026-000123, razon social, NIF, matricula" data-testid="hist-buscar"/>
            </Field>
          </div>
          <div className="w-[13.75rem] min-w-[13.75rem] shrink-0">
            <Field label="Desde">
              <DatePicker value={q.desde} onChange={(v)=>setQ({...q, desde:v})} testid="hist-desde"/>
            </Field>
          </div>
          <div className="w-[13.75rem] min-w-[13.75rem] shrink-0">
            <Field label="Hasta">
              <DatePicker value={q.hasta} onChange={(v)=>setQ({...q, hasta:v})} testid="hist-hasta"/>
            </Field>
          </div>
          <div className="w-full lg:w-[140px] shrink-0">
            <Label htmlFor="hist-estado" className="label-tag">Estado</Label>
            <Select value={q.estado || "all"} onValueChange={(v)=>setQ({...q, estado: v==="all" ? "" : v})}>
              <SelectTrigger id="hist-estado" data-testid="hist-estado"><SelectValue placeholder="Todos"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="generado">Generado</SelectItem>
                <SelectItem value="modificado">Modificado</SelectItem>
                <SelectItem value="anulado">Anulado</SelectItem>
                <SelectItem value="borrador">Borrador</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      <Card className="overflow-hidden" hidden={view !== "table"}>
        <div className="overflow-x-auto">
          <table className="deca-table w-full min-w-[900px]">
            <thead>
              <tr>
                <SortableTh label={"N" + String.fromCharCode(0xba) + " DeCA"} sortKey="id" sort={sort} onSort={toggle} />
                <SortableTh label="Fecha emisión" sortKey="fecha" sort={sort} onSort={toggle} />
                <SortableTh label="Cargador" sortKey="cargador" sort={sort} onSort={toggle} />
                <SortableTh label="Transportista" sortKey="transportista" sort={sort} onSort={toggle} />
                <SortableTh label="Envíos" sortKey="envios" sort={sort} onSort={toggle} align="center" />
                <SortableTh label="Matrículas" sortKey="matriculas" sort={sort} onSort={toggle} />
                <SortableTh label="Ver." sortKey="version" sort={sort} onSort={toggle} />
                <SortableTh label="Estado" sortKey="estado" sort={sort} onSort={toggle} />
                <th></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((d) => (
                <tr key={d.id} data-testid={`row-${d.id}`}>
                  <td className="font-mono text-xs whitespace-nowrap">{fmtNumeroDeca(d)}</td>
                  <td className="whitespace-nowrap">{fmtFechaHora(d.created_at)}</td>
                  <td>
                    <div className="font-medium">{d.cargador_snapshot.razon_social}</div>
                    <div className="text-xs text-slate-500 font-mono">{d.cargador_snapshot.nif}</div>
                  </td>
                  <td>
                    <div className="font-medium">{d.transportista_snapshot.razon_social}</div>
                    <div className="text-xs text-slate-500 font-mono">{d.transportista_snapshot.nif}</div>
                  </td>
                  <td className="text-center font-mono font-semibold">{d.num_envios}</td>
                  <td className="font-mono text-xs">{(d.matriculas || []).join(", ")}</td>
                  <td className="text-center font-mono">v{d.version}</td>
                  <td><StatusBadge estado={d.estado}/></td>
                  <td className="text-right">
                    <div className="flex justify-end gap-2">
                      <a href={decaPdfPath(d.id)} target="_blank" rel="noreferrer">
                        <Button size="sm" variant="outline" data-testid={`btn-pdf-${d.id}`}><FileText className="h-3.5 w-3.5 mr-1.5"/> PDF</Button>
                      </a>
                      <Button size="sm" className="bg-blue-700 hover:bg-blue-800" onClick={()=>nav(`/decas/${d.id}`)} data-testid={`btn-open-${d.id}`}>
                        <Eye className="h-3.5 w-3.5 mr-1.5"/> Abrir
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {sorted.length === 0 && !loading ? (
                <tr><td colSpan={9} className="py-12 text-center text-slate-500">{allItems.length === 0 ? "No hay DeCA registrados todavía." : "No hay DeCA con esos filtros."}</td></tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </Card>

      {view === "timeline" ? (
        <DecaTimeline items={filtered} />
      ) : null}
    </div>
  );
}
